import './style.scss';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getIncidenceActions } from '../../API/apiService';
import { downloadIncidenceAttendance093 } from '../../utils/incidenceActionAttendance';
import EvidenceReviewStatus from '../../components/evidenceReviewStatus';

function formatDate(value) {
  if (!value) return 'Sin fecha';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat('es-CO', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).format(date);
}

function IncidenceActions() {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let active = true;
    getIncidenceActions().then(data => {
      if (!active) return;
      setActions(data);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  const filteredActions = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('es');
    if (!query) return actions;
    return actions.filter(action => [
      action.name,
      action.topic,
      action.municipality,
      action.neighborhood,
      action.place,
      action.address,
      action.groupNames,
      action.mediators
    ].some(value => String(value || '').toLocaleLowerCase('es').includes(query)));
  }, [actions, search]);

  async function downloadAttendance(action) {
    setDownloadingId(action.id);
    try {
      await downloadIncidenceAttendance093(action);
      toast('Descarga de asistencia iniciada', { type: 'success' });
    } catch (error) {
      toast('No fue posible descargar la asistencia', { type: 'error' });
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <section className='incidence-actions-page'>
      <div className='incidence-actions-card'>
        <header className='incidence-actions-header'>
          <div>
            <p>GESTIÓN DE EVIDENCIAS</p>
            <h1>Acciones de incidencia</h1>
            <span>Consulta las acciones registradas y actualiza su información o asistencia.</span>
          </div>
          <Link to='/acciones-incidencia/nueva'>
            <span className='material-symbols-rounded' aria-hidden='true'>add</span>
            Cargar nueva acción
          </Link>
        </header>

        <label className='incidence-actions-search'>
          <span className='material-symbols-rounded' aria-hidden='true'>search</span>
          <input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder='Buscar por nombre, tema, grupo o mediador'
          />
        </label>

        {loading ? (
          <p className='incidence-actions-state'>Cargando acciones...</p>
        ) : filteredActions.length === 0 ? (
          <p className='incidence-actions-state'>
            {search ? 'No hay acciones que coincidan con la búsqueda.' : 'Aún no hay acciones de incidencia registradas.'}
          </p>
        ) : (
          <div className='incidence-actions-list'>
            {filteredActions.map(action => (
              <article key={action.id} className='incidence-actions-item'>
                <div className='incidence-actions-item__main'>
                  <div>
                    <span className='incidence-actions-item__date'>
                      {formatDate(action.actionDate)} · {action.startTime}–{action.endTime}
                    </span>
                    <h2>{action.name}</h2>
                    <p>{action.topic}</p>
                    <EvidenceReviewStatus
                      status={action.reviewStatus}
                      observations={action.reviewObservations}
                    />
                  </div>
                  <div className='incidence-actions-item__actions'>
                    {action.hasAttendance && (
                      <button
                        type='button'
                        disabled={downloadingId === action.id}
                        onClick={() => downloadAttendance(action)}>
                        <span className='material-symbols-rounded' aria-hidden='true'>download</span>
                        {downloadingId === action.id ? 'Generando...' : 'Descargar asistencia'}
                      </button>
                    )}
                    <Link to={`/acciones-incidencia/${action.id}`}>
                      Ver y editar
                      <span className='material-symbols-rounded' aria-hidden='true'>arrow_forward</span>
                    </Link>
                  </div>
                </div>
                <dl>
                  <div><dt>Ubicación</dt><dd>{action.municipality}, {action.neighborhood}</dd></div>
                  <div><dt>Lugar y dirección</dt><dd>{action.place} · {action.address}</dd></div>
                  <div><dt>Grupos</dt><dd>{action.groupNames || 'Sin grupos vinculados'}</dd></div>
                  <div><dt>Mediadores</dt><dd>{action.mediators}</dd></div>
                  <div><dt>Participantes</dt><dd>{action.participantCount || 0}</dd></div>
                </dl>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default IncidenceActions;
